import React, { useEffect, useMemo, useState } from 'react';
import { dbService } from '../../services/DatabaseService';
import { requireAuth } from '../../pocketbaseClient';
import { StatFilters } from '../statistiken/StatFilters';
import { KpiStrip } from '../statistiken/KpiStrip';
import { ScoreDistributionChart } from '../statistiken/ScoreDistributionChart';
import { RecommendationDonut } from '../statistiken/RecommendationDonut';
import { StatCard } from '../statistiken/StatCard';
import { SentVsSubmittedChart } from '../statistiken/SentVsSubmittedChart';
import { BewerbungsflussSankey } from '../statistiken/BewerbungsflussSankey';
import { QuestionnaireComparisonTable } from '../statistiken/QuestionnaireComparisonTable';
import { AnswerAnalysis } from '../statistiken/AnswerAnalysis';
import {
  filterByRange, filterByTemplate, templateOptions, filterTokensByCreated, computeKpis,
  scoreDistribution, recommendationSplit, sentVsSubmittedOverTime, statusFlow,
  perQuestionnaireStats, perTemplateStats, perQuestionAnswerStats
} from '../../utils/statisticsAggregation';

export function StatistikenPage({ navigateTo }) {
  const [loading, setLoading] = useState(true);
  const [subs, setSubs] = useState([]);
  const [tokens, setTokens] = useState([]);
  const [questionnaires, setQuestionnaires] = useState([]);
  const [tiers, setTiers] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState('all');
  const [rangeDays, setRangeDays] = useState(30);
  // The completion timer occasionally logged runaway durations (hours). Ignore
  // anything over 2 h in the time stats by default; the user can switch it off.
  const [ignoreOutliers, setIgnoreOutliers] = useState(true);
  // Local selection for the Antwort-Analyse widget (independent of the global
  // Fragebogen filter, since answer analysis needs exactly one questionnaire).
  const [answerQuestionnaireId, setAnswerQuestionnaireId] = useState('');

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      try {
        requireAuth();
        const [s, t, ques, settings] = await Promise.all([
          dbService.getSubmissionsForStats(),
          dbService.getAccessTokensForStats(),
          dbService.getQuestionnairesForList(),
          dbService.getSettings()
        ]);
        if (!active) return;
        setSubs(s);
        setTokens(t);
        setQuestionnaires(ques);
        setTiers(settings?.recommendationTiers || null);
      } catch (e) {
        console.error('Error loading statistics:', e);
        if (active) { setSubs([]); setTokens([]); setQuestionnaires([]); }
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, []);

  const questionnairesById = useMemo(
    () => Object.fromEntries(questionnaires.map(q => [q.id, q])), [questionnaires]);

  const templateList = useMemo(() => templateOptions(questionnairesById), [questionnairesById]);

  const subsInScope = useMemo(() => {
    const now = new Date();
    return filterByRange(filterByTemplate(subs, questionnairesById, selectedTemplate), rangeDays, now);
  }, [subs, questionnairesById, selectedTemplate, rangeDays]);

  const tokensInScope = useMemo(() => {
    const now = new Date();
    return filterTokensByCreated(filterByTemplate(tokens, questionnairesById, selectedTemplate), rangeDays, now);
  }, [tokens, questionnairesById, selectedTemplate, rangeDays]);

  // Previous period (same length, immediately before) for vs-Vorperiode deltas.
  const { prevSubs, prevTokens } = useMemo(() => {
    if (rangeDays === 'all') return { prevSubs: [], prevTokens: [] };
    const now = new Date();
    const prevEnd = new Date(now.getTime() - rangeDays * 24 * 60 * 60 * 1000);
    const inPrev = (d) => d instanceof Date
      && d.getTime() >= prevEnd.getTime() - rangeDays * 24 * 60 * 60 * 1000
      && d.getTime() < prevEnd.getTime();
    const qSubs = filterByTemplate(subs, questionnairesById, selectedTemplate);
    const qTokens = filterByTemplate(tokens, questionnairesById, selectedTemplate);
    return {
      prevSubs: qSubs.filter(s => inPrev(s.submittedAt)),
      prevTokens: qTokens.filter(t => inPrev(t.createdAt))
    };
  }, [subs, tokens, questionnairesById, selectedTemplate, rangeDays]);

  const comparisonRows = useMemo(
    () => perQuestionnaireStats(subsInScope, questionnairesById, tiers),
    [subsInScope, questionnairesById, tiers]);
  const templateRows = useMemo(
    () => perTemplateStats(subsInScope, questionnairesById, tiers, { ignoreOutliers }),
    [subsInScope, questionnairesById, tiers, ignoreOutliers]);

  const answerStats = useMemo(() => {
    if (!answerQuestionnaireId) return [];
    const q = questionnairesById[answerQuestionnaireId];
    if (!q) return [];
    const subs = subsInScope.filter((s) => s.questionnaireId === answerQuestionnaireId);
    return perQuestionAnswerStats(subs, q);
  }, [subsInScope, questionnairesById, answerQuestionnaireId]);

  const kpis = useMemo(() => computeKpis(subsInScope, tokensInScope, tiers), [subsInScope, tokensInScope, tiers]);
  const prevKpis = useMemo(() => computeKpis(prevSubs, prevTokens, tiers), [prevSubs, prevTokens, tiers]);
  const scoreDist = useMemo(() => scoreDistribution(subsInScope, tiers), [subsInScope, tiers]);
  const recSplit = useMemo(() => recommendationSplit(subsInScope, tiers), [subsInScope, tiers]);
  const trend = useMemo(() => sentVsSubmittedOverTime(tokensInScope, subsInScope, rangeDays, new Date()), [tokensInScope, subsInScope, rangeDays]);
  const flow = useMemo(() => statusFlow(subsInScope), [subsInScope]);

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-800 dark:text-slate-100">Statistiken</h1>
        <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
          Analysiere die Ergebnisse und Trends deiner Fragebögen.
        </p>
      </div>

      <StatFilters
        templates={templateList}
        templateName={selectedTemplate}
        onTemplateChange={setSelectedTemplate}
        rangeDays={rangeDays}
        onRangeChange={setRangeDays}
      />

      {!loading && subs.length === 0 && (
        <div className="text-center text-slate-400 py-16">Noch keine Ergebnisse vorhanden.</div>
      )}

      {loading
        ? <div className="flex items-center justify-center py-20">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
              <p className="mt-4 text-slate-600 dark:text-slate-400">Statistiken werden geladen …</p>
            </div>
          </div>
        : subs.length === 0
          ? null
          : <div className="space-y-10">
              <KpiStrip current={kpis} previous={prevKpis} />

              <section>
                <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400" style={{ marginTop: '1.25rem', marginBottom: '1.25rem' }}>Qualität</h2>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: '1.25rem' }}>
                  <StatCard title="Score-Verteilung" subtitle="Gesamtscore der ausgewerteten Ergebnisse">
                    <ScoreDistributionChart data={scoreDist} />
                  </StatCard>
                  <StatCard title="Empfehlungen" subtitle="Aufteilung nach Empfehlungsstufe">
                    <RecommendationDonut data={recSplit} />
                  </StatCard>
                </div>
              </section>

              <section>
                <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400" style={{ marginTop: '1.25rem', marginBottom: '1.25rem' }}>Fluss &amp; Ergebnisse</h2>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: '1.25rem' }}>
                  <StatCard title="Fragebögen vs. Ergebnisse" subtitle="Versendete Links (blau) und alle Ergebnisse (grün) im Zeitverlauf">
                    <SentVsSubmittedChart data={trend} />
                  </StatCard>
                  <StatCard title="Bewerbungsfluss" subtitle={`${flow.hired} Zusagen von ${flow.total} Ergebnissen`}>
                    <BewerbungsflussSankey flow={flow} />
                  </StatCard>
                </div>
                <div className="mt-6">
                  <StatCard title="Vorlagen-Vergleich" subtitle="Ergebnisse, Qualität und Bearbeitungszeit je Vorlage">
                    <QuestionnaireComparisonTable
                      rows={templateRows}
                      ignoreOutliers={ignoreOutliers}
                      onToggleOutliers={setIgnoreOutliers}
                    />
                  </StatCard>
                </div>
              </section>

              <section>
                <StatCard title="Antwort-Analyse" subtitle="Antwortverteilung und Übersprung-Rate je Frage">
                  <AnswerAnalysis
                    options={comparisonRows}
                    selectedId={answerQuestionnaireId}
                    onSelect={setAnswerQuestionnaireId}
                    questions={answerStats}
                  />
                </StatCard>
              </section>
            </div>}
    </div>
  );
}
